import type { ReactNode } from 'react';

/**
 * Editorial section header used across landing + state hub pages.
 * Eyebrow → title → optional sub copy → optional action slot.
 *
 *   <SectionHeader
 *     eyebrow="Practice areas"
 *     title="Find a lawyer for your injury"
 *     sub="Browse by case type and connect with a verified attorney near you."
 *     action={<Btn kind="ghost">All practice areas</Btn>}
 *   />
 */

export interface SectionHeaderProps {
    eyebrow?: string;
    title: string;
    sub?: string;
    action?: ReactNode;
    align?: 'left' | 'center';
    as?: 'h1' | 'h2' | 'h3';
    className?: string;
}

export function SectionHeader({
    eyebrow,
    title,
    sub,
    action,
    align = 'left',
    as: Heading = 'h2',
    className,
}: SectionHeaderProps) {
    const isCenter = align === 'center';
    const cls = [
        'flex gap-6',
        isCenter
            ? 'flex-col items-center text-center'
            : 'flex-col md:flex-row md:items-end md:justify-between',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <header className={cls}>
            <div className={isCenter ? 'max-w-2xl' : 'max-w-2xl'}>
                {eyebrow !== undefined && (
                    <div className="text-[12px] font-medium tracking-widest uppercase text-ember mb-2">
                        {eyebrow}
                    </div>
                )}
                <Heading className="text-[28px] md:text-[36px] leading-tight">
                    {title}
                </Heading>
                {sub !== undefined && (
                    <p className="text-ink-soft text-[16px] md:text-[18px] mt-2 leading-relaxed">
                        {sub}
                    </p>
                )}
            </div>
            {action !== undefined && (
                <div className={isCenter ? 'mt-2' : 'shrink-0'}>{action}</div>
            )}
        </header>
    );
}

export default SectionHeader;
